import { injectIntl, IntlShape, WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import React, { useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Tab } from 'semantic-ui-react';
import { Helmet } from 'react-helmet';
import { useDatasetDescription } from '../../../shared/api/apiHooks';
import { DatasetTabParam, getDatasetsPath, getMainDataPath, getSingleDatasetPath } from '../dataExplorerRouterPaths';
import { DescriptionTab, FieldsTab } from './DatasetTabs';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { fieldFiltersDefaultValues, fieldFiltersTransformers } from '../FieldFilters/fieldFilterConfig';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { CommonDataFieldsSelector, getDefaultCommonDataFilterValues } from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import { useFilterForm } from '../Filters/useFilterForm';
import { addModifiers } from '../../../shared/helpers';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import '../Data.scss';
import { DataExplorerLink, useDataExplorerHistory } from '../dataExplorerHistoryContext';

interface DatasetRouteProps {
  datasetId: string;
  tab?: DatasetTabParam;
}

function getTabPanes({
  datasetId,
  intl,
  defaultValues,
  control,
  queryObject,
  reset,
}: {
  datasetId: string;
  intl: IntlShape;
  defaultValues: any;
  control: FiltersControl;
  queryObject: any;
  reset: () => void;
}) {
  return [
    {
      menuItem: formatMessageHelper(intl, 'dataExplorer.tabs.fields'),
      render: () => (
        <FieldsTab defaultValues={defaultValues} control={control} queryObject={queryObject} reset={reset} datasetId={datasetId} />
      ),
    },
    {
      menuItem: formatMessageHelper(intl, 'dataExplorer.tabs.description'),
      render: () => <DescriptionTab control={control} datasetId={datasetId} />,
    },
  ];
}

const idTabMap = {
  '0': 'fields',
  '1': 'description',
} as const;

const tabIdMap = {
  fields: '0',
  description: '1',
} as const;

function Dataset({ match, intl }: RouteComponentProps<DatasetRouteProps> & InjectedIntlProps) {
  const datasetId = match.params.datasetId;
  const history = useDataExplorerHistory();
  const tab = match.params.tab ?? idTabMap[0];

  const tabIndex = tabIdMap[tab];

  const trackEvent = useDataExplorerTrackCustomEvent();

  const onTabChange = (activeTabId?: '0' | '1') => {
    const tab = idTabMap[activeTabId ?? tabIdMap.fields] as DatasetTabParam;
    trackEvent(`de_click_${tab}_tab`);
    history.push(getSingleDatasetPath({ datasetId, tab, query: history.location.search }));
  };

  const { data: datasetDescription } = useDatasetDescription({ id: datasetId });

  const defaultValues = useMemo(
    () => ({ ...fieldFiltersDefaultValues, [filterFields.datasetId.name]: datasetId, ...getDefaultCommonDataFilterValues() }),
    []
  );

  const { control, reset, debouncedQueryObject } = useFilterForm({
    fieldTransformers: fieldFiltersTransformers,
    defaultValues: defaultValues,
  });

  if (!datasetId) {
    return <div>No dataset id — no dataset</div>;
  }

  return (
    <div className="data-main">
      <Helmet title={datasetDescription?.name} />
      <div className={addModifiers('data__header-container', tab === 'description' ? ['sticky'] : [])}>
        <div className="data-breadcrumbs">
          <Breadcrumbs
            items={[
              {
                link: getMainDataPath(),
                LinkComponent: DataExplorerLink,
                name: <FormattedMessageHelper id="dataExplorer.title" />,
                onClickCapture: () => {
                  trackEvent('de_click_breadcrumb_main');
                },
              },
              {
                link: getDatasetsPath(),
                name: <FormattedMessageHelper id="dataExplorer.breadcrumbs.browseDatasets" />,
                LinkComponent: DataExplorerLink,
                onClickCapture: () => trackEvent('de_click_breadcrumb_datasets_list'),
              },
              { name: datasetDescription?.name },
            ]}
          />
        </div>
        <CommonDataFieldsSelector control={control} />
      </div>
      <Tab
        onTabChange={(_, data) => {
          onTabChange(data.activeIndex as any);
        }}
        activeIndex={tabIndex}
        panes={getTabPanes({ datasetId, intl, defaultValues, control, queryObject: debouncedQueryObject, reset })}
      />
    </div>
  );
}

export default injectIntl(Dataset);
